import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getCategories } from 'services/cataloge';
import { ICategory } from 'types/category';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './CategoryDropdown.scss';

interface ICategoryDropdown {
  value: string;
  onChange: (id: number, name: string) => void;
}

/**
 * Selector de categoría con filtro local y apertura por foco.
 * Carga las categorías del catálogo al montar y filtra la lista según lo que el usuario escribe.
 * El dropdown se renderiza en un portal para escapar el overflow:hidden del contenedor padre.
 */
const CategoryDropdown = ({ value, onChange }: ICategoryDropdown) => {
  const [searchString, setSearchString] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);

  const isFilled = !!value;

  const filteredCategories = categories.filter(c =>
    c.name?.toLowerCase().includes(searchString.toLowerCase())
  );

  /** Mantiene valueRef sincronizado para que handleClickOutside siempre lea el valor actual. */
  useEffect(() => {
    valueRef.current = value;
    setSearchString(value);
  }, [value]);

  /** Carga las categorías del catálogo al montar el componente. */
  useEffect(() => {
    setIsLoading(true);
    getCategories()
      .then(data => setCategories(data.filter(c => c.id !== null && c.name !== null)))
      .catch(() => displayErrorNotification())
      .finally(() => setIsLoading(false));
  }, []);

  /**
   * Cierra el dropdown al hacer click fuera. Se crea una sola vez (deps: []).
   * Lee valueRef.current en lugar de capturar value del closure para evitar stale state.
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const isInsideContainer = containerRef.current?.contains(e.target as Node);
      const isInsideDropdown = dropdownRef.current?.contains(e.target as Node);
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
        setSearchString(valueRef.current);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Calcula la posición del dropdown midiendo el espacio disponible arriba y abajo del input.
   * Si hay más espacio arriba (o no cabe mínimamente abajo), abre hacia arriba (flip).
   */
  const updateDropdownPosition = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const maxHeight = 200;
    const minUsable = 120;

    if (spaceBelow >= minUsable || spaceBelow >= spaceAbove) {
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(maxHeight, spaceBelow),
        zIndex: 9999
      });
    } else {
      const availableHeight = Math.min(maxHeight, spaceAbove);
      setDropdownStyle({
        position: 'fixed',
        top: rect.top - availableHeight - 4,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight,
        zIndex: 9999
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    updateDropdownPosition();
    setIsOpen(true);
  };

  const handleSelect = (category: ICategory) => {
    setSearchString(category.name!);
    setIsOpen(false);
    onChange(category.id!, category.name!);
  };

  return (
    <div className={`categoryDropdown${isFilled ? ' filled' : ''}`} ref={containerRef}>
      <div className="inputWrapper">
        <input
          ref={inputRef}
          value={searchString}
          placeholder={isLoading ? 'Cargando...' : 'Seleccionar categoría'}
          disabled={isLoading}
          onChange={handleInputChange}
          onFocus={handleFocus}
          autoComplete="off"
        />
      </div>
      {isOpen && createPortal(
        <div className="categoryDropdownList" style={dropdownStyle} ref={dropdownRef}>
          {filteredCategories.length === 0 && (
            <div className="dropdownItem empty">Sin resultados</div>
          )}
          {filteredCategories.map(category => (
            <div
              key={category.id!}
              className="dropdownItem"
              onMouseDown={() => handleSelect(category)}
            >
              {category.name}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default CategoryDropdown;
